// components/reports/ComparisonReport.jsx
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { CustomLineChart } from '../../components/charts/LineChart';
import { CustomBarChart } from '../../components/charts/BarChart';
import { Badge } from '../../../../components/ui/badge';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

export function ComparisonReport({ 
  currentData, 
  previousData, 
  period = 'month' 
}) {
  const [comparisonType, setComparisonType] = useState('sales');

  // Mock comparison data (you would fetch this from your API)
  const comparisonData = {
    sales: [
      { name: 'Today', current: currentData?.Table?.[0]?.NetSale || 0, previous: 45000 },
      { name: 'Yesterday', current: 52000, previous: 48000 },
      { name: 'This Week', current: 350000, previous: 320000 },
      { name: 'This Month', current: 1500000, previous: 1400000 },
    ],
    orders: [
      { name: 'Today', current: currentData?.Table?.[0]?.TotalOrders_Landed || 0, previous: 18 },
      { name: 'Yesterday', current: 25, previous: 22 },
      { name: 'This Week', current: 150, previous: 140 },
      { name: 'This Month', current: 600, previous: 580 },
    ],
    customers: [
      { name: 'Today', current: 22, previous: 20 },
      { name: 'Yesterday', current: 30, previous: 28 },
      { name: 'This Week', current: 180, previous: 170 },
      { name: 'This Month', current: 750, previous: 720 },
    ],
  };

  const getComparisonChartData = () => {
    const data = comparisonData[comparisonType] || comparisonData.sales;
    return data.map(item => ({
      name: item.name,
      Current: item.current,
      Previous: item.previous,
    }));
  };

  const calculateGrowth = (current, previous) => {
    if (!previous) return 0;
    return ((current - previous) / previous) * 100;
  };

  const GrowthIndicator = ({ growth }) => {
    if (growth > 0) {
      return (
        <div className="flex items-center text-green-600">
          <TrendingUp className="h-4 w-4 mr-1" />
          <span>+{growth.toFixed(1)}%</span>
        </div>
      );
    } else if (growth < 0) {
      return (
        <div className="flex items-center text-red-600">
          <TrendingDown className="h-4 w-4 mr-1" />
          <span>{growth.toFixed(1)}%</span>
        </div>
      );
    }
    return (
      <div className="flex items-center text-gray-600">
        <Minus className="h-4 w-4 mr-1" />
        <span>0%</span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Comparison Type Selector */}
      <div className="flex gap-2">
        {['sales', 'orders', 'customers'].map((type) => (
          <button
            key={type}
            onClick={() => setComparisonType(type)}
            className={`px-4 py-2 rounded-lg capitalize ${
              comparisonType === type
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {type}
          </button>
        ))}
      </div>

      {/* Comparison Chart */}
      <CustomLineChart
        data={getComparisonChartData()}
        title={`${comparisonType.toUpperCase()} Comparison: Current vs Previous ${period}`}
        lines={[
          { dataKey: 'Current', color: '#3b82f6', name: 'Current Period' },
          { dataKey: 'Previous', color: '#94a3b8', name: 'Previous Period' },
        ]}
      />

      {/* KPI Comparison Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {comparisonData.sales.map((item, index) => {
          const growth = calculateGrowth(item.current, item.previous);
          return (
            <Card key={index}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">{item.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold">
                      {comparisonType === 'sales' 
                        ? `Rs ${item.current.toLocaleString()}`
                        : item.current}
                    </span>
                    <GrowthIndicator growth={growth} />
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Previous: {comparisonType === 'sales' 
                      ? `Rs ${item.previous.toLocaleString()}`
                      : item.previous}
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full"
                      style={{ 
                        width: `${Math.min((item.current / (item.previous * 1.5)) * 100, 100)}%` 
                      }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detailed Comparison Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium">Metric</th>
                  <th className="text-left py-3 px-4 font-medium">Current Period</th>
                  <th className="text-left py-3 px-4 font-medium">Previous Period</th>
                  <th className="text-left py-3 px-4 font-medium">Growth</th>
                  <th className="text-left py-3 px-4 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium">Total Sales</td>
                  <td className="py-3 px-4">Rs {currentData?.Table?.[0]?.NetSale?.toLocaleString() || '0'}</td>
                  <td className="py-3 px-4">Rs 45000</td>
                  <td className="py-3 px-4">
                    <GrowthIndicator growth={calculateGrowth(
                      currentData?.Table?.[0]?.NetSale || 0, 
                      45000
                    )} />
                  </td>
                  <td className="py-3 px-4">
                    <Badge className="bg-green-100 text-green-800">Positive</Badge>
                  </td>
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium">Total Orders</td>
                  <td className="py-3 px-4">{currentData?.Table?.[0]?.TotalOrders_Landed || 0}</td>
                  <td className="py-3 px-4">18</td>
                  <td className="py-3 px-4">
                    <GrowthIndicator growth={calculateGrowth(
                      currentData?.Table?.[0]?.TotalOrders_Landed || 0, 
                      18
                    )} />
                  </td>
                  <td className="py-3 px-4">
                    <Badge className="bg-green-100 text-green-800">Positive</Badge>
                  </td>
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium">Average Order Value</td>
                  <td className="py-3 px-4">
                    Rs {currentData?.Table?.[0]?.NetSale 
                      ? Math.round(currentData.Table[0].NetSale / currentData.Table[0].TotalOrders_Landed)
                      : 0}
                  </td>
                  <td className="py-3 px-4">Rs 2500</td>
                  <td className="py-3 px-4">
                    <GrowthIndicator growth={calculateGrowth(
                      currentData?.Table?.[0]?.NetSale 
                        ? Math.round(currentData.Table[0].NetSale / currentData.Table[0].TotalOrders_Landed)
                        : 0, 
                      2500
                    )} />
                  </td>
                  <td className="py-3 px-4">
                    <Badge className="bg-green-100 text-green-800">Positive</Badge>
                  </td>
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium">Cancellation Rate</td>
                  <td className="py-3 px-4">
                    {currentData?.Table1?.find(s => s.OrderStatus === 'Cancel')?.Percentage || 0}%
                  </td>
                  <td className="py-3 px-4">15%</td>
                  <td className="py-3 px-4">
                    <GrowthIndicator growth={calculateGrowth(
                      currentData?.Table1?.find(s => s.OrderStatus === 'Cancel')?.Percentage || 0, 
                      15
                    )} />
                  </td>
                  <td className="py-3 px-4">
                    <Badge className="bg-red-100 text-red-800">Needs Attention</Badge>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}